/**
 * PowersureDeviceGridSection — herbruikbare apparaten-grid.
 *
 * 7 apparaattegels (P1, omvormer, laadpaal, warmtepomp, boiler, batterij,
 * slimme stekker) + "handmatig/onbekend" route. Visueel kerntje uit het
 * conceptbeeld: laat zien wat Powersure kan koppelen en welke status elk
 * apparaat krijgt. Geen live-control claims.
 */
import {
  Activity,
  Cpu,
  Plug,
  Flame,
  Droplets,
  BatteryCharging,
  Sparkles,
  HelpCircle,
  ArrowRight,
} from 'lucide-react';
import PowersureCard from './PowersureCard';
import DeviceTile from './DeviceTile';
import SectionHeader from './SectionHeader';

const DEVICES = [
  { id: 'p1',          icon: Activity,         name: 'P1-meter',       hint: 'Hele woning realtime in beeld', status: 'read-only' },
  { id: 'omvormer',    icon: Cpu,              name: 'Omvormer',       hint: 'Zonne­paneel-productie per uur', status: 'read-only' },
  { id: 'laadpaal',    icon: Plug,             name: 'Laadpaal',       hint: 'Slim laden op zon of dal-tarief', status: 'sturing' },
  { id: 'warmtepomp',  icon: Flame,            name: 'Warmtepomp',     hint: 'Verbruik en pieken inzichtelijk', status: 'installateur' },
  { id: 'boiler',      icon: Droplets,         name: 'Boiler',         hint: 'Warm tapwater plannen op zon', status: 'handmatig' },
  { id: 'batterij',    icon: BatteryCharging,  name: 'Thuisbatterij',  hint: 'Opslag voor avondverbruik',      status: 'later' },
  { id: 'plug',        icon: Sparkles,         name: 'Slimme stekker', hint: 'Wasmachine, droger, vaatwasser', status: 'sturing' },
  { id: 'unknown',     icon: HelpCircle,       name: 'Anders / weet niet', hint: 'We helpen u in 1 stap kiezen', status: 'handmatig' },
];

export default function PowersureDeviceGridSection({
  onTileClick,
  onAddClick,
  title = 'Apparaten koppelen',
  subtitle = 'Powersure ondersteunt deze apparaten. Per tegel ziet u welke route mogelijk is.',
  showHeader = true,
  className = '',
  testId = 'ps-device-grid-section',
}) {
  return (
    <section
      className={`container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 ${className}`}
      data-testid={testId}
    >
      <PowersureCard padding="p-5 sm:p-7" className="max-w-3xl mx-auto">
        {showHeader && (
          <SectionHeader
            label="Apparaten"
            title={title}
            subtitle={subtitle}
            Icon={Plug}
          />
        )}
        <div className={`${showHeader ? 'mt-5' : ''} grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3`}>
          {DEVICES.map((d) => (
            <DeviceTile
              key={d.id}
              icon={d.icon}
              name={d.name}
              hint={d.hint}
              status={d.status}
              onClick={() => onTileClick?.(d)}
              testId={`${testId}-tile-${d.id}`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={onAddClick}
          data-testid={`${testId}-add-btn`}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-sm font-semibold transition-colors shadow-lg shadow-emerald-900/40"
        >
          Apparaat toevoegen
          <ArrowRight className="w-4 h-4" />
        </button>
        <p className="text-[11px] text-white/45 mt-3 leading-relaxed text-center">
          Eerst inzicht, dan advies, dan eventueel sturing — alleen met uw goedkeuring.
        </p>
      </PowersureCard>
    </section>
  );
}
